import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export function InboxTab({ tickets, reload }) {
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const openTicket = (t) => { setActive(t); setReply(""); };
  const send = async () => {
    if (!active || !reply.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/support/tickets/${active.id}/reply`, { body: reply });
      if (r.data.reply.email_sent) toast.success(`Reply emailed to ${active.user_email}`);
      else toast.warning(`Reply logged (email failed: ${r.data.reply.email_error || "provider off"})`);
      setReply("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  };
  const setStatus = async (s) => {
    if (!active) return;
    try { await api.patch(`/support/tickets/${active.id}`, { status: s }); toast.success(s); reload(); }
    catch { toast.error("Failed"); }
  };
  useEffect(() => {
    if (active) {
      const fresh = tickets.find(t => t.id === active.id);
      if (fresh) setActive(fresh);
    }
  }, [tickets]);

  const statusColor = {
    open: "bg-[#D96C4A]/15 text-[#B04E2F]",
    in_progress: "bg-[#CBA35C]/20 text-[#8C6B21]",
    answered: "bg-[#184A30]/10 text-[#184A30]",
    closed: "bg-neutral-200 text-neutral-700",
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-2 bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden" data-testid="inbox-list">
        {tickets.length === 0 && <p className="p-6 text-sm text-[#4A5C50]">No support tickets yet.</p>}
        {tickets.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => openTicket(t)}
            className={`w-full text-left px-5 py-4 border-b border-[#E5E0D8] last:border-0 transition ${active?.id === t.id ? "bg-[#184A30]/5" : "hover:bg-[#FBF9F6]"}`}
            data-testid={`ticket-${t.id}`}
          >
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${statusColor[t.status] || statusColor.open}`}>{t.status.replace("_", " ")}</span>
              <span className="text-xs text-[#4A5C50] mono ml-auto">{new Date(t.updated_at).toLocaleDateString()}</span>
            </div>
            <p className="font-medium mt-2 truncate">{t.subject}</p>
            <p className="text-xs text-[#4A5C50] mono truncate">{t.user_email}</p>
          </button>
        ))}
      </div>
      <div className="md:col-span-3">
        {active ? (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="inbox-detail">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">From {active.user_name || active.user_email}</p>
                <h3 className="serif text-2xl mt-1">{active.subject}</h3>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${statusColor[active.status] || statusColor.open}`}>{active.status.replace("_", " ")}</span>
            </div>
            <p className="mt-5 text-sm whitespace-pre-wrap leading-relaxed text-[#112318]">{active.message}</p>

            {(active.replies || []).map((r) => (
              <div key={r.id} className="mt-5 p-4 bg-[#184A30]/5 border border-[#184A30]/10 rounded-xl">
                <p className="text-[11px] tracking-widest uppercase text-[#184A30]">{r.from_name} · {new Date(r.sent_at).toLocaleString()}</p>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.body}</p>
                <p className="text-[10px] mt-2 text-[#4A5C50] mono">{r.email_sent ? "Emailed ✓" : `Email error: ${r.email_error || "—"}`}</p>
              </div>
            ))}

            <div className="mt-6">
              <Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Reply by email</Label>
              <Textarea rows={5} value={reply} onChange={(e) => setReply(e.target.value)} className="mt-2 rounded-xl border-[#E5E0D8]" data-testid="ticket-reply-input" />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={send} disabled={sending || !reply.trim()} className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="ticket-reply-send">
                  <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send reply"}
                </Button>
                <Button variant="outline" className="rounded-xl" onClick={() => setStatus("in_progress")}>Mark in progress</Button>
                <Button variant="outline" className="rounded-xl" onClick={() => setStatus("closed")}>Close</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 text-sm text-[#4A5C50]">Select a ticket to view & reply.</div>
        )}
      </div>
    </div>
  );
}
